import { AuthLetterMismatch } from "../../../../data-model/cptCodesTableModel";
import { AuthLetterReviewStatus } from "../../../../enums/authLetterReviewStatus";
import DocWithUploadIcon from "../../../../svg/doc-with-upload-icon";
import DocumentUploadSuccess from "../../../../svg/documentUploadSuccess";
import InvalidFieldsWarning from "./InvalidFieldsWarning";
import MismatchWarning from "./MismatchWarning";

/** New letter uploaded: pale peach bg, terracotta stroke */
const NEW_LETTER_BG = "#FFF3E0";
const NEW_LETTER_STROKE = "#C24400";
/** Reviewed and saved: light green bg, dark green stroke */
const REVIEWED_BG = "#E6F3F0";
const REVIEWED_STROKE = "#005D49";

interface IsDocumentUploadedProps {
  isUploaded: boolean;
  isVisible: boolean;
  orderId: string;
  jCode: string;
  jCodeId: string;
  authStatus: string;
  authLetterReviewStatus?: string;
  mismatchFields?: AuthLetterMismatch[];
  /**
   * Required auth-letter fields holding invalid values. Shown as a red count
   * badge only on the dark-green (unreviewed) icon, where the user cannot reach
   * the light-green reviewed state while these stay invalid.
   */
  invalidFieldNames?: string[];
  onUploadClick: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  onSuccessClick: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
}

/**
 * Renders the CPT-table document upload / success icon. When auth-letter field
 * mismatches exist, overlays a red count badge that shows the mismatch details
 * on hover without intercepting the icon click.
 *
 * @input props - Upload visibility, review status, mismatch fields, and click handlers
 * @return JSX.Element | null: Centered document icon with optional mismatch badge
 */
const IsDocumentUploaded = (props: IsDocumentUploadedProps) => {
  if (!props.isVisible) {
    return null;
  }
  const isNewLetterUploaded =
    props.authLetterReviewStatus === AuthLetterReviewStatus.NewLetterUploaded;
  const isReviewedAndSaved =
    props.authLetterReviewStatus === AuthLetterReviewStatus.ReviewedAndSaved;

  const handleUploadClick = () => {
    props.onUploadClick(
      props.orderId,
      props.jCode,
      props.jCodeId,
      props.authStatus,
      props.authLetterReviewStatus,
    );
  };
  const handleSuccessClick = () => {
    props.onSuccessClick(
      props.orderId,
      props.jCode,
      props.jCodeId,
      props.authStatus,
      props.authLetterReviewStatus,
    );
  };

  const getIcon = () => {
    if (isNewLetterUploaded) {
      return (
        <DocumentUploadSuccess
          height={"20px"}
          width={"20px"}
          backgroundColor={NEW_LETTER_BG}
          strokeColor={NEW_LETTER_STROKE}
        />
      );
    }
    if (isReviewedAndSaved) {
      return (
        <DocumentUploadSuccess
          height={"20px"}
          width={"20px"}
          backgroundColor={REVIEWED_BG}
          strokeColor={REVIEWED_STROKE}
        />
      );
    }
    if (props.isUploaded) {
      return (
        <DocumentUploadSuccess
          height={"20px"}
          width={"20px"}
          backgroundColor="#00775E"
          strokeColor="white"
        />
      );
    }
    return <DocWithUploadIcon height={"20px"} width={"20px"} color="#0056D6" />;
  };

  const mismatchCount = props.mismatchFields?.length ?? 0;
  const isUnreviewed =
    props.isUploaded && !isNewLetterUploaded && !isReviewedAndSaved;
  const actionLabel = props.isUploaded
    ? "View auth letter"
    : "Upload auth letter";
  const buttonAriaLabel =
    mismatchCount > 0
      ? `${actionLabel}. ${mismatchCount} auth letter field mismatch${
          mismatchCount === 1 ? "" : "es"
        }`
      : actionLabel;

  return (
    <div className="table-cell-document-uploaded flex h-full w-full items-center justify-center">
      <div
        className="document-upload-icon--container relative flex items-center justify-center rounded-[0.25rem]"
        style={{
          cursor: "pointer",
          ...(isReviewedAndSaved && { backgroundColor: REVIEWED_BG }),
        }}
        onClick={props.isUploaded ? handleSuccessClick : handleUploadClick}
        role="button"
        aria-label={buttonAriaLabel}
      >
        {getIcon()}
        <MismatchWarning mismatchFields={props.mismatchFields} />
        {isUnreviewed && (
          <InvalidFieldsWarning fieldNames={props.invalidFieldNames} />
        )}
      </div>
    </div>
  );
};

export default IsDocumentUploaded;
